import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    FiArrowLeft, FiSearch, FiX, FiFileText, FiChevronRight, FiInbox, FiAlertCircle, FiRotateCcw,
    FiCheckCircle, FiTool, FiExternalLink, FiPlus, FiUserCheck,
} from "react-icons/fi";
import HrNewRequestDrawer from "../../../pages/hr/HrNewRequestDrawer";
import { useDostepneTypyWnioskow } from "../../../hooks/useDostepneTypyWnioskow";
import {
    opisTypuWniosku, szukajTypowWnioskow, coDalejZWnioskiem, opisKategoriiWnioskow, KTO_AKCEPTUJE_PRZED_ZLOZENIEM, odmiana,
} from "../../../utils/hrOpisTypuWniosku";
import { localizedPath } from "../../../hooks/useLocalizedNavigate";
import PustyStan from "../../common/PustyStan/PustyStan";
import LadowanieStrony from "../../common/Ladowanie/LadowanieStrony";
import { liczbaKolumnSiatki, rozpietosciSekcji } from "./ukladSiatkiOkien";
/* Kafelki, nagłówek, wyszukiwarka, rama formularza i przyciski — z arkusza okna zgłoszenia, żeby wniosek
   wyglądał DOKŁADNIE jak zgłoszenie (jedno źródło wyglądu, zero kopii CSS). Własny arkusz: lista typów z opisem. */
import s from "./MyNewTicketModal.module.css";
import k from "./KartaWnioskowPracowniczych.module.css";

/**
 * KARTA „WNIOSKI PRACOWNICZE” w oknie „Utwórz nowe zgłoszenie”.
 *
 * [polecenie właściciela 2026-09-28] „jak wnioski pracownicze, to co mozna zlozyc w sekcji mpracownik.krosno.com/
 * wnioski-hr - nowy wniosek w takim samym wygladzie jak zgloszenie z opisem jaki wniosek co i jak (ale z opcja wyboru
 * kategorii a potem listy) i caly proces uzupelnienia wniosku, pamietaj zeby byla zgodnosc okien modal itp”.
 *
 * PRZEBIEG: kafle KATEGORII (ikona, opis z nazw jej wniosków, liczba wniosków) → lista TYPÓW z opisem „co to, kiedy,
 * co potrzebne” (albo wyszukiwarka po wszystkich typach) → KREATOR wniosku w tym samym oknie → potwierdzenie z numerem,
 * PRAWDZIWĄ ścieżką akceptacji (kroki utworzone przez serwer przy zapisie) i linkiem do wniosku.
 *
 * [poprawka po przeglądzie 2026-09-28] „Kto akceptuje” NIE jest liczone z typu: lista `available-types` celowo nie
 * niesie ścieżki akceptacji, a pierwsza wersja podstawiała wtedy „przełożony → HR” każdemu wnioskowi. Przed złożeniem
 * lista mówi raz, uczciwie, gdzie ścieżkę zobaczyć; po złożeniu potwierdzenie pokazuje kroki z odpowiedzi serwera.
 * Po wysłaniu karta rozgłasza „hr-request-changed” (jak powiadomienie SignalR), żeby /wnioski-hr i Mój Dział dołożyły
 * nowy wiersz bez F5 — z `wlasnaAkcja`, bo własny wniosek nie jest „nową aktywnością” i nie zapala kropki w menu.
 *
 * ŹRÓDŁA — te same co w /wnioski-hr, bez żadnej kopii logiki:
 *  - lista typów: `useDostepneTypyWnioskow` (ten sam hak, którego używa kreator z /wnioski-hr → to samo
 *    `GET /hr-requests/available-types`, te same uprawnienia typów i to samo zawężenie do wniosków osobistych);
 *  - formularz, walidacja, prefill, salda, spółka, załączniki i wysyłka: `HrNewRequestDrawer` w oprawie „okno”
 *    (ten sam komponent co w /wnioski-hr i w Mój Dział — tam w oprawie „szuflada”, bez zmian).
 *
 * Zamknięcie: WYŁĄCZNIE Modal gospodarza (✕ / Anuluj; tło nie zamyka — W-10). Gdy człowiek coś już wpisał,
 * gospodarz pyta przed porzuceniem (`potwierdzPorzucenie`) — tak samo przy „Wróć do listy”.
 *
 * @param {Function} onZamknij             Anuluj / zamknięcie po wysłaniu — zamyka okno (gospodarz decyduje o pytaniu)
 * @param {Function} onWypelnienie         (bool) — czy w formularzu wniosku jest coś wpisanego
 * @param {Function} potwierdzPorzucenie   () => Promise<boolean> — pytanie przed porzuceniem wpisanych danych
 * @param {Function} onDoSprawTechnicznych przejście na kartę „Sprawy techniczne” (CTA pustego stanu)
 */

const WIDOK = Object.freeze({ kategorie: "kategorie", typy: "typy", formularz: "formularz", wyslano: "wyslano" });

/* Rama kreatora w oknie = rama formularza zgłoszenia (te same klasy: nagłówek z „Wróć”, przewijana treść, stopka). */
const KLASY_OKNA = Object.freeze({
    widok: s.formView,
    naglowek: s.formHeader,
    wstecz: s.backPill,
    info: s.headerInfo,
    ikona: s.headerIcon,
    tytul: s.headerTitle,
    tresc: s.formBody,
    stopka: s.formFooter,
    anuluj: s.btnCancel,
    glowny: s.btnSubmit,
});

const WIERSZE_OPISU = [
    ["co", "Co to za wniosek"],
    ["kiedy", "Kiedy go składać"],
    ["coPotrzebne", "Co będzie potrzebne"],
];

const stylIkony = (kat) => (kat?.color ? { color: kat.color, background: kat.bg || `${kat.color}1F` } : undefined);

const KartaWnioskowPracowniczych = ({ onZamknij, onWypelnienie, potwierdzPorzucenie, onDoSprawTechnicznych }) => {
    const { categories, allTypes, typesError, odswiez } = useDostepneTypyWnioskow(true);
    const dostepneTypy = useMemo(() => ({ categories, allTypes, typesError }), [categories, allTypes, typesError]);

    const [widok, setWidok] = useState(WIDOK.kategorie);
    const [idKategorii, setIdKategorii] = useState(null);
    const [typ, setTyp] = useState(null);
    const [fraza, setFraza] = useState("");
    const [zlozony, setZlozony] = useState(null);

    /* „Coś wpisane” trzymamy też w referencji: pytanie o porzucenie czyta ją w chwili kliknięcia, a nie w chwili
       ostatniego renderu (zmiana pola i klik „Wróć do listy” potrafią przyjść w jednym przebiegu). */
    const wypelnionyRef = useRef(false);
    const ustawWypelnienie = useCallback((tak) => {
        wypelnionyRef.current = !!tak;
        if (typeof onWypelnienie === "function") onWypelnienie(!!tak);
    }, [onWypelnienie]);

    const kategoria = useMemo(
        () => (categories || []).find((c) => c.id === idKategorii) || null,
        [categories, idKategorii]
    );
    const wyniki = useMemo(() => szukajTypowWnioskow(categories, fraza), [categories, fraza]);
    const szukanie = !!fraza.trim();

    /* Siatka kategorii liczy kolumny i rozciąga niepełny rząd TĄ SAMĄ regułą co siatka okien zgłoszeń. */
    const liczbaKategorii = (categories || []).length;
    const kolumny = liczbaKolumnSiatki(liczbaKategorii);
    const rozpietosci = useMemo(() => rozpietosciSekcji(liczbaKategorii, kolumny), [liczbaKategorii, kolumny]);

    /* Fokus po zmianie widoku idzie na pierwszy element nowego widoku (klawiatura nie ląduje „nigdzie”, gdy kliknięty
       kafelek znika). NIE przy pierwszym wejściu w kartę — tam fokus zostaje na zakładce (WAI-ARIA tabs). */
    const korzenRef = useRef(null);
    /* Porównanie z POPRZEDNIM widokiem (a nie flaga „pierwszy raz”): podwójny przebieg efektów w trybie ścisłym
       Reacta nie może zabrać fokusu z zakładki przy samym wejściu w kartę. */
    const poprzedniWidok = useRef(widok);
    useEffect(() => {
        if (poprzedniWidok.current === widok) return;
        poprzedniWidok.current = widok;
        const el = korzenRef.current?.querySelector("button, input, a[href]");
        if (el && typeof el.focus === "function") el.focus();
    }, [widok]);

    const wybierzKategorie = (kat) => { setIdKategorii(kat.id); setWidok(WIDOK.typy); };
    const wybierzTyp = (t, kat) => {
        if (kat) setIdKategorii(kat.id);
        setTyp(t);
        setWidok(WIDOK.formularz);
    };
    const wrocDoKategorii = () => { setIdKategorii(null); setWidok(WIDOK.kategorie); };

    const pytajOPorzucenie = useCallback(async () => {
        if (!wypelnionyRef.current) return true;
        if (typeof potwierdzPorzucenie !== "function") return false;
        return !!(await potwierdzPorzucenie());
    }, [potwierdzPorzucenie]);

    /* „Wróć do listy” z formularza: po wyszukiwaniu — do wyników, po kategorii — do jej listy. */
    const wrocZFormularza = useCallback(async () => {
        if (!(await pytajOPorzucenie())) return;
        ustawWypelnienie(false);
        setTyp(null);
        setWidok(!fraza.trim() && idKategorii ? WIDOK.typy : WIDOK.kategorie);
    }, [pytajOPorzucenie, ustawWypelnienie, fraza, idKategorii]);

    const poWyslaniu = useCallback((wniosek) => {
        ustawWypelnienie(false);
        setZlozony(wniosek || {});
        setWidok(WIDOK.wyslano);
        /* Żywy moduł (W-3): serwer nie powiadamia SKŁADAJĄCEGO o jego własnym wniosku, a szuflada w /wnioski-hr odświeża
           listę przez własne `onRequestCreated` — okno zgłoszenia nie miało żadnej drogi. Rozgłaszamy to samo zdarzenie okna,
           którym SignalR ożywia strony wniosków (HrRequestsPage, widok działu, e-teczka…). `wlasnaAkcja` = bez kropki. */
        try {
            window.dispatchEvent(new CustomEvent("hr-request-changed", {
                detail: { wlasnaAkcja: true, zrodlo: "okno-nowego-zgloszenia", requestId: wniosek?.id ?? null },
            }));
        } catch { /* środowisko bez CustomEvent — lista odświeży się przy następnym wejściu */ }
    }, [ustawWypelnienie]);

    const zlozKolejny = () => {
        setZlozony(null);
        setTyp(null);
        setIdKategorii(null);
        setFraza("");
        setWidok(WIDOK.kategorie);
        /* Lista „co mogę złożyć” od nowa — wniosek jednorazowy (np. rezygnacja z PPK) po złożeniu znika z serwera
           i nie może dalej wisieć w oknie, żeby odmowa przyszła dopiero po wypełnieniu całego formularza. */
        odswiez();
    };

    const zamknij = () => { if (typeof onZamknij === "function") onZamknij(); };

    /* ── Jedna karta typu: nazwa + cztery odpowiedzi z danych typu (brak opisu = tekst zastępczy). ── */
    const renderTyp = (t, kat, zKategoria = false) => {
        const IkonaTypu = t.icon || kat?.icon || FiFileText;
        const opis = opisTypuWniosku(t, kat?.label);
        return (
            <button
                key={`${kat?.id || "?"}:${t.id}`}
                type="button"
                className={k.typ}
                onClick={() => wybierzTyp(t, kat)}
                data-testid="typ-wniosku"
                data-kod={t.code}
            >
                <span className={k.typGora}>
                    <span className={k.typIkona} style={stylIkony(kat)} aria-hidden="true"><IkonaTypu size={16} /></span>
                    <span className={k.typNazwa}>{t.label}</span>
                    {zKategoria && kat?.label && <span className={k.typKategoria}>{kat.label}</span>}
                    <FiChevronRight size={16} className={k.typStrzalka} aria-hidden="true" />
                </span>
                <span className={k.typOpis}>
                    {WIERSZE_OPISU.map(([klucz, etykieta]) => (
                        <span key={klucz} className={`${k.typWiersz} ${klucz === "co" ? k.typWierszPelny : ""}`} data-opis={klucz}>
                            <span className={k.typEtykieta}>{etykieta}</span>
                            <span className={k.typTresc}>{opis[klucz]}</span>
                        </span>
                    ))}
                </span>
            </button>
        );
    };

    /* ── Stany listy: ładowanie / awaria / nic nie przysługuje ── */
    const renderStanListy = () => {
        if (categories === null) {
            return <LadowanieStrony wariant="sekcja" etykieta="Wczytywanie wniosków, które możesz złożyć…" />;
        }
        if (typesError) {
            return (
                <PustyStan
                    blad
                    testId="wnioski-blad"
                    ikona={<FiAlertCircle />}
                    tytul="Nie udało się pobrać listy wniosków"
                    opis="Spróbuj ponownie za chwilę. Jeżeli to się powtarza, zgłoś problem w karcie „Sprawy techniczne”."
                    akcja={{ etykieta: "Spróbuj ponownie", onClick: odswiez, ikona: <FiRotateCcw size={14} /> }}
                />
            );
        }
        if (categories.length === 0) {
            return (
                <PustyStan
                    testId="wnioski-pusto"
                    ikona={<FiInbox />}
                    tytul="Nie ma wniosków, które możesz złożyć"
                    opis={"Zestaw wniosków zależy od Twojej umowy, wymiaru etatu i przyznanych pul (np. urlopowej). "
                        + "Jeżeli czegoś tu brakuje, napisz do działu kadr — sprawę kadrową zgłosisz w karcie „Sprawy techniczne”."}
                    akcja={typeof onDoSprawTechnicznych === "function"
                        ? { etykieta: "Przejdź do spraw technicznych", onClick: onDoSprawTechnicznych, ikona: <FiTool size={14} /> }
                        : null}
                />
            );
        }
        return null;
    };

    /* Jedna uczciwa linijka „kto akceptuje” pod listą typów (zamiast zgadywania przy każdym typie). */
    const renderUwagaAkceptacji = () => (
        <p className={k.uwagaAkceptacji} data-testid="kto-akceptuje-przed-zlozeniem">
            <FiUserCheck size={14} aria-hidden="true" />
            <span><strong>Kto akceptuje?</strong> {KTO_AKCEPTUJE_PRZED_ZLOZENIEM}</span>
        </p>
    );

    /* ═══ WIDOK: KATEGORIE (+ wyszukiwarka po wszystkich typach) ═══ */
    const renderKategorie = () => {
        const stan = renderStanListy();
        return (
            <div className={s.selectionView} data-testid="wnioski-kategorie">
                <div className={s.heading}>
                    <h3 className={s.title}>Jaki wniosek chcesz złożyć?</h3>
                    <p className={s.subtitle}>
                        Wybierz kategorię, a potem wniosek — przy każdym jest opis: co to za wniosek, kiedy go składać i co będzie potrzebne
                    </p>
                </div>

                {stan || (
                    <>
                        <div className={s.szukajkaOkien}>
                            <FiSearch className={s.szukajkaIkona} size={14} aria-hidden="true" />
                            <input
                                type="search"
                                className={s.szukajkaPole}
                                value={fraza}
                                onChange={(e) => setFraza(e.target.value)}
                                placeholder="Szukaj wniosku — np. „urlop”, „zaświadczenie”, „PPK”"
                                aria-label="Szukaj wniosku"
                                data-testid="szukajka-wnioskow"
                            />
                            {!!fraza && (
                                <button type="button" className={s.szukajkaCzysc} onClick={() => setFraza("")}
                                    aria-label="Wyczyść frazę wyszukiwania">
                                    <FiX size={13} />
                                </button>
                            )}
                        </div>

                        {szukanie ? (
                            wyniki.length === 0 ? (
                                <div className={`${s.emptyTilesState} ${s.pustyWynikSzukania}`} data-testid="pusty-wynik-wnioskow">
                                    <FiSearch size={26} />
                                    <b>Żaden wniosek nie pasuje do wyszukiwania</b>
                                    <p>Nie znaleźliśmy wniosku dla frazy „{fraza.trim()}”. Spróbuj krótszego słowa albo wybierz kategorię.</p>
                                    <button type="button" className={s.pustyWynikReset} onClick={() => setFraza("")}>
                                        Wyczyść wyszukiwanie
                                    </button>
                                </div>
                            ) : (
                                <div className={`${k.listaTypow} ${k.listaWynikow}`} data-testid="wyniki-wnioskow">
                                    {wyniki.map(({ typ: t, kategoria: kat }) => renderTyp(t, kat, true))}
                                    {renderUwagaAkceptacji()}
                                </div>
                            )
                        ) : (
                            <div className={s.grid} style={{ "--kolumny-siatki": kolumny }} data-kolumny={kolumny}
                                data-testid="siatka-kategorii-wnioskow">
                                {categories.map((kat, i) => {
                                    const IkonaKategorii = kat.icon || FiFileText;
                                    const ile = kat._typesCount ?? (kat.types || []).length;
                                    const rozpietosc = rozpietosci[i] || 1;
                                    return (
                                        <button
                                            key={kat.id}
                                            type="button"
                                            className={`${s.card} ${s.cardBlue} ${rozpietosc > 1 ? s.cardRozciagniety : ""}`}
                                            style={rozpietosc > 1 ? { gridColumn: `span ${rozpietosc}` } : undefined}
                                            data-rozpietosc={rozpietosc}
                                            onClick={() => wybierzKategorie(kat)}
                                            data-testid="kafelek-kategorii-wnioskow"
                                        >
                                            <div className={s.cardIconBox} style={stylIkony(kat)}><IkonaKategorii /></div>
                                            <span className={s.cardLabel}>{kat.label}</span>
                                            {/* Kategoria nie ma w bazie opisu — kafel mówi, jakie wnioski są w środku (z nazw typów). */}
                                            <span className={s.cardHint} data-testid="opis-kategorii-wnioskow">
                                                {opisKategoriiWnioskow(kat)}
                                            </span>
                                            <span className={s.cardDeptBadge}>
                                                <FiFileText size={11} /> {ile} {odmiana(ile, ["wniosek", "wnioski", "wniosków"])}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    /* ═══ WIDOK: TYPY W KATEGORII ═══ */
    const renderTypy = () => {
        if (!kategoria) return renderKategorie();
        const IkonaKategorii = kategoria.icon || FiFileText;
        return (
            <div className={s.formView} data-testid="wnioski-typy">
                <div className={s.formHeader}>
                    <button type="button" onClick={wrocDoKategorii} className={s.backPill}>
                        <FiArrowLeft size={14} /> Wróć
                    </button>
                    <div className={s.headerInfo}>
                        <div className={s.headerIcon} style={stylIkony(kategoria)} aria-hidden="true"><IkonaKategorii /></div>
                        <div className={k.naglowekTekst}>
                            <h4 className={s.headerTitle}>{kategoria.label}</h4>
                            <p className={k.naglowekOpis}>
                                Wybierz wniosek — przy każdym jest opis: co to za wniosek, kiedy go składać i co będzie potrzebne.
                            </p>
                        </div>
                    </div>
                </div>
                <div className={s.formBody}>
                    <div className={k.listaTypow}>
                        {(kategoria.types || []).map((t) => renderTyp(t, kategoria))}
                    </div>
                    {renderUwagaAkceptacji()}
                </div>
            </div>
        );
    };

    /* ═══ WIDOK: WNIOSEK ZŁOŻONY ═══ */
    const renderWyslano = () => {
        const numer = zlozony?.requestNumber || "";
        const link = zlozony?.id != null
            ? `${localizedPath("hrRequests")}?req=${encodeURIComponent(zlozony.id)}`
            : localizedPath("hrRequests");
        return (
            <div className={k.wyslano} role="status" data-testid="wniosek-wyslany">
                <span className={k.wyslanoIkona} aria-hidden="true"><FiCheckCircle size={28} /></span>
                <h3 className={k.wyslanoTytul}>{numer ? `Wniosek ${numer} został złożony` : "Wniosek został złożony"}</h3>
                {/* Ścieżka z ODPOWIEDZI SERWERA (kroki utworzone przy zapisie), nie z typu — przed złożeniem jej nie znamy. */}
                <p className={k.wyslanoOpis} data-testid="wniosek-wyslany-co-dalej">
                    {coDalejZWnioskiem(zlozony, typ?.label)}{" "}
                    Status i historię wniosku zobaczysz w „Wnioski HR”.
                </p>
                <div className={k.wyslanoAkcje}>
                    <button type="button" className={s.btnCancel} onClick={zlozKolejny}>
                        <FiPlus size={14} aria-hidden="true" /> Złóż kolejny wniosek
                    </button>
                    <Link to={link} className={s.btnSubmit} onClick={zamknij} data-testid="link-do-wniosku">
                        <FiExternalLink size={14} aria-hidden="true" /> Otwórz wniosek
                    </Link>
                </div>
            </div>
        );
    };

    return (
        <div ref={korzenRef} className={k.karta} data-widok-wnioskow={widok}>
            {widok === WIDOK.kategorie && renderKategorie()}
            {widok === WIDOK.typy && renderTypy()}
            {widok === WIDOK.formularz && typ && (
                <HrNewRequestDrawer
                    key={typ.id}
                    oprawa="okno"
                    isOpen
                    typDomyslny={typ.id}
                    dostepneTypy={dostepneTypy}
                    klasyOkna={KLASY_OKNA}
                    onClose={zamknij}
                    onWstecz={wrocZFormularza}
                    onWypelnienie={ustawWypelnienie}
                    onRequestCreated={poWyslaniu}
                />
            )}
            {widok === WIDOK.wyslano && renderWyslano()}
        </div>
    );
};

export default KartaWnioskowPracowniczych;
